import { useMemo } from 'react';

import WheelPicker from '../WheelPicker';
import styles from './styles.module.scss';

/**
 * Three-column month / day / year birth-date wheel.
 *
 * Extracted from `common/chat/BirthDateModal` in HORO-10533 so the Profile v2
 * "Edit Birth Date" screen and the horoscope birth-date modal share one
 * picker — and, more importantly, one definition of how an out-of-range day
 * is handled. Two copies of that rule would eventually disagree about
 * February.
 *
 * Controlled: the caller owns `{ month, day, year }` and receives changes.
 * Clamping is deliberately NOT applied here (see `clampDay`) — the day column
 * stays a constant 1–31 so a user can flick freely without numbers vanishing
 * underneath them as they roll past a short month. The caller clamps once, on
 * confirm.
 */
export const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];

export const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const FIRST_YEAR = 1920;

export const buildYears = () => {
    const currentYear = new Date().getUTCFullYear();
    const out = [];
    for (let y = FIRST_YEAR; y <= currentYear; y += 1) out.push(y);
    return out;
};

export const lastDayOfMonth = (monthIndex, year) => new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

/** Nearest valid day in the chosen month — Feb 30 becomes Feb 28 or 29. */
export const clampDay = (day, monthIndex, year) => Math.min(day, lastDayOfMonth(monthIndex, year));

/**
 * Build the ISO string to persist.
 *
 * Constructed in LOCAL time deliberately — `Date.UTC` would store midnight
 * UTC, which renders as the *previous* calendar day for any browser west of
 * UTC (pick May 29 in Pacific time, store 2001-05-29T00:00Z, redisplay as
 * May 28). The saved instant must round-trip to the same date the user picked.
 */
export const toIsoBirthDate = ({ month, day, year }) => {
    const monthIndex = MONTHS.indexOf(month);
    return new Date(year, monthIndex, clampDay(day, monthIndex, year)).toISOString();
};

/** Split a stored ISO date back into wheel positions, or null if unparseable. */
export const fromIsoBirthDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return { month: MONTHS[d.getMonth()], day: d.getDate(), year: d.getFullYear() };
};

const BirthDatePicker = ({ month, day, year, onMonthChange, onDayChange, onYearChange }) => {
    const years = useMemo(buildYears, []);

    return (
        <div className={styles.wheels}>
            <WheelPicker items={MONTHS} value={month} onChange={onMonthChange} ariaLabel="Month" />
            <WheelPicker items={DAYS} value={day} onChange={onDayChange} ariaLabel="Day" />
            <WheelPicker items={years} value={year} onChange={onYearChange} ariaLabel="Year" />
        </div>
    );
};

export default BirthDatePicker;
